import { useEffect, useState } from "react";
import PageMeta from "../components/PageMeta";
import { useLanguage } from "../i18n/LanguageProvider";

const API_BASE_URL = (
  process.env.REACT_APP_PUBLIC_API_URL ||
  process.env.REACT_APP_API_URL ||
  "https://app-api.wonyasoft.com"
).replace(/\/$/, "");

export function localizedPartnerDetail(partner, language) {
  const primary = language === "en" ? partner.detailEn : partner.detailFr;
  const fallback = language === "en" ? partner.detailFr : partner.detailEn;
  return String(primary || fallback || "").trim();
}

export default function Clients() {
  const { language } = useLanguage();
  const [state, setState] = useState({
    loading: true,
    content: null,
    error: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (process.env.NODE_ENV === "test") {
      setState({ loading: false, content: null, error: "" });
      return undefined;
    }

    const controller = new AbortController();
    fetch(`${API_BASE_URL}/public/site-trust`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Clients unavailable");
        return response.json();
      })
      .then((response) => {
        setState({
          loading: false,
          content: response?.data || null,
          error: "",
        });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setState({
            loading: false,
            content: null,
            error:
              language === "en"
                ? "Unable to load the client references."
                : "Impossible de charger les références clients.",
          });
        }
      });
    return () => controller.abort();
  }, [language]);

  const title = state.content
    ? language === "en"
      ? state.content.titleEn || state.content.titleFr
      : state.content.titleFr || state.content.titleEn
    : language === "en"
      ? "Our clients"
      : "Nos clients";
  const partners = state.content?.partners || [];

  return (
    <div className="ws-clients-page">
      <PageMeta
        title={`${title} | WonyaSoft`}
        description="Découvrez les références clients publiées par WonyaSoft."
      />
      <header className="ws-content-archive__hero">
        <div className="ws-shell">
          <span className="ws-kicker">
            {language === "en"
              ? "TRUSTED REFERENCES"
              : "RÉFÉRENCES DE CONFIANCE"}
          </span>
          <h1>{title}</h1>
          <p>
            {language === "en"
              ? "Organizations whose references are currently published."
              : "Les organisations dont la référence est actuellement publiée."}
          </p>
        </div>
      </header>
      <section
        className="ws-shell ws-clients-page__section"
        aria-busy={state.loading}
        aria-live="polite"
      >
        {state.loading ? (
          <p>{language === "en" ? "Loading…" : "Chargement…"}</p>
        ) : state.error ? (
          <p role="alert">{state.error}</p>
        ) : partners.length === 0 ? (
          <p>
            {language === "en"
              ? "No client reference is currently published."
              : "Aucune référence client n’est publiée pour le moment."}
          </p>
        ) : (
          <div className="ws-clients-page__grid">
            {partners.map((partner) => {
              const detail = localizedPartnerDetail(partner, language);

              return (
                <article className="ws-clients-page__card" key={partner.id}>
                  <img
                    src={partner.logoUrl}
                    alt={partner.altText || partner.name}
                    loading="lazy"
                    width="220"
                    height="100"
                  />
                  <div className="ws-clients-page__copy">
                    <h2>{partner.name}</h2>
                    {detail ? <p>{detail}</p> : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
